import { useEffect, useCallback } from 'react'
import { useMutation, useApolloClient } from '@apollo/client'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { Announcement } from '../../types/announcement'
import { isUnread, needsAcknowledgement } from '../../types/announcement'
import {
  MARK_ANNOUNCEMENT_READ,
  ACKNOWLEDGE_ANNOUNCEMENT,
} from '../../graphql/announcement-mutations'
import { ANNOUNCEMENT_FEED } from '../../graphql/announcement-queries'
import { formatRelativeTime } from './announcementFormat'
import AnnouncementTypeBadge from './AnnouncementTypeBadge'
import AcknowledgeSlider from './AcknowledgeSlider'

interface Props {
  announcement: Announcement | null
  onClose: () => void
}

// Reader-facing detail popup: full body, mark-read on open, and acknowledge.
// Authoring/receipts live in the separate admin flow, not here.
const AnnouncementModal = ({ announcement, onClose }: Props) => {
  const client = useApolloClient()

  const [markRead] = useMutation(MARK_ANNOUNCEMENT_READ)
  const [acknowledge] = useMutation(ACKNOWLEDGE_ANNOUNCEMENT)

  const id = announcement?.id
  const unread = !!announcement && isUnread(announcement)

  // Refetch every active feed query at once — the nav badge, dashboard widget and
  // feed page each hold their own cache entry (different limits), so read/ack must
  // update them all to keep unread counts and markers consistent.
  const refreshFeeds = useCallback(() => {
    void client.refetchQueries({ include: [ANNOUNCEMENT_FEED] })
  }, [client])

  // Opening the announcement marks it read (idempotent server-side). Fire only
  // when there's a receipt to update — a null receipt means no read obligation.
  useEffect(() => {
    if (id == null || !unread) return
    markRead({ variables: { id } })
      .then(() => refreshFeeds())
      .catch(() => { /* reading shouldn't fail loudly; leave the row as-is */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (!announcement) return null

  const author = announcement.author?.name
  const when = formatRelativeTime(announcement.published_at ?? announcement.created_at)

  const handleAcknowledge = async () => {
    await acknowledge({ variables: { id: announcement.id } })
    refreshFeeds()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative inline-block w-full max-w-2xl transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
            <div className="min-w-0">
              <AnnouncementTypeBadge type={announcement.type} />
              <h2 className="mt-2 text-lg font-semibold text-gray-900">{announcement.title}</h2>
              <p className="mt-0.5 text-xs text-gray-400">
                {author ? `${author} · ` : ''}{when}
              </p>
            </div>
            <button onClick={onClose} className="flex-shrink-0 text-gray-400 hover:text-gray-600" aria-label="Close">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="max-h-[55vh] overflow-y-auto px-6 py-5">
            <p className="whitespace-pre-line text-sm leading-relaxed text-gray-800">{announcement.body}</p>
          </div>

          {/* Footer — acknowledge only */}
          {needsAcknowledgement(announcement) && (
            <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
              <AcknowledgeSlider onConfirm={handleAcknowledge} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AnnouncementModal
